import { Arc, Line } from "react-konva";
import { useMemo } from "react";

import { COLOR_BRAND_BLUE_900 } from "../../../constants";

export type WordopolisCellCords = {
  x: number;
  y: number;
};

export interface MarkProps {
  color?: string;
  start: WordopolisCellCords;
  end: WordopolisCellCords;
  size: number;
}

const MARK_SIZE_MODIFIER = 2.3;

export const Mark = (props: MarkProps) => {
  const { start, end, size, color = COLOR_BRAND_BLUE_900 } = props;

  const { firstArc, secondArc, centralLine, compassReading } = useMemo(() => {
    const centralLine = {
      start: {
        x: start.x * size + size / 2,
        y: start.y * size + size / 2,
      },
      end: {
        x: end.x * size + size / 2,
        y: end.y * size + size / 2,
      },
    };

    const radius = size / MARK_SIZE_MODIFIER;

    const radians = Math.atan2(centralLine.end.y - centralLine.start.y, centralLine.end.x - centralLine.start.x);
    const compassReading = radians * (180 / Math.PI) - 90;

    const countArcJoin = (x: number, y: number, angle: number, radius: number) => {
      const angleInRadians = (angle * Math.PI) / 180;
      const x1 = x + radius * Math.cos(angleInRadians);
      const y1 = y + radius * Math.sin(angleInRadians);
      return { x: x1, y: y1 };
    };

    const firstArc = {
      start: countArcJoin(centralLine.start.x, centralLine.start.y, compassReading + 180, radius),
      end: countArcJoin(centralLine.end.x, centralLine.end.y, compassReading + 180, radius),
    };
    const secondArc = {
      start: countArcJoin(centralLine.start.x, centralLine.start.y, compassReading, radius),
      end: countArcJoin(centralLine.end.x, centralLine.end.y, compassReading, radius),
    };

    return {
      firstArc,
      secondArc,
      centralLine,
      compassReading,
    };
  }, [start, end, size]);

  return (
    <>
      {[firstArc, secondArc].map((arcJoins, index) => (
        <Line
          key={index}
          points={[arcJoins.start.x, arcJoins.start.y, arcJoins.end.x, arcJoins.end.y]}
          stroke={color}
          strokeWidth={size / 10}
          z-index={100}
        />
      ))}
      {Object.values(centralLine).map((point, index) => (
        <Arc
          key={index}
          x={point.x}
          y={point.y}
          innerRadius={size / MARK_SIZE_MODIFIER}
          outerRadius={size / MARK_SIZE_MODIFIER}
          stroke={color}
          strokeWidth={size / 10}
          angle={180}
          rotation={compassReading + (index === 0 ? 180 : 0)}
        />
      ))}
    </>
  );
};
